import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight, Phone, ChevronDown, ChevronUp, Building2, Factory, Warehouse, Hospital } from "lucide-react";
import SEO from "../components/SEO";

export default function ScaffoldingRentalPage() {
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    document.title = "Scaffolding Rental India - Cuplock, Props & Systems | Amar Preet";
  }, []);

  const features = [
    "High-quality ISO certified equipment",
    "Fast mobilization from multi-city inventory",
    "Flexible rental periods",
    "Technical support and guidance",
    "Competitive pricing",
    "No maintenance burden"
  ];

  const scaffoldingTypes = [
    {
      name: "Cuplock Scaffolding",
      description: "Versatile modular system for high-rise and complex structures",
      features: ["High load capacity", "Quick assembly", "Flexible configurations"],
      link: "/cuplock-scaffolding"
    },
    {
      name: "Shuttering System",
      description: "Complete formwork and shuttering material for concrete construction",
      features: ["Slab & beam formwork", "Column boxes & plates", "Reusable steel components"],
      link: "/shuttering-material-on-rent"
    },
    {
      name: "Industrial Scaffolding",
      description: "Heavy-duty solutions for plants, refineries, and industrial sites",
      features: ["Safety compliant", "High durability", "Custom configurations"],
      link: "/industrial-scaffolding"
    }
  ];

  const industries = [
    { icon: Building2, name: "High-Rise Residential" },
    { icon: Warehouse, name: "Commercial Projects" },
    { icon: Factory, name: "Industrial Plants" },
    { icon: Hospital, name: "Institutional Buildings" }
  ];

  const faqs = [
    {
      question: "What types of scaffolding are available for rent?",
      answer: "We offer Cuplock scaffolding systems, frame scaffolding, industrial scaffolding, MS pipes & tubes, steel plates & planks, couplers & joints, props & jacks, and Acrospan formwork systems."
    },
    {
      question: "What is the minimum rental period?",
      answer: "The minimum rental period is 3 months. We offer competitive pricing for long-term rentals."
    },
    {
      question: "Which cities do you serve?",
      answer: "We serve in all cities across India, having our inventory and operations in Delhi, Noida, Hyderabad & Vizag."
    },
    {
      question: "Do you provide delivery and installation support?",
      answer: "We provide delivery to your project site but we do not provide installation support. We only supply the scaffolding material on rent. Your team or contractor handles the erection and dismantling on site."
    }
  ];

  return (
    <div data-testid="scaffolding-rental-page">
      <SEO title="Scaffolding Rental" description="Premium scaffolding on rent across India. Cuplock, industrial & high-rise scaffolding. 30+ years experience, 200+ clients. Get quote from Amar Preet Scaffolding." canonical="/scaffolding-rental" />
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-yellow-500 font-semibold text-sm tracking-wider uppercase">Services</span>
              <h1 className="text-4xl md:text-6xl font-bold mt-4 mb-6 leading-tight" data-testid="page-headline">
                Scaffolding Rental Solutions for Every Project
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed mb-8">
                Premium scaffolding rental services across India. From high-rise residential to industrial plants, we provide quality equipment with fast mobilization and technical support.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-yellow-500 hover:bg-yellow-600 text-slate-900 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 inline-flex items-center justify-center"
                  data-testid="hero-cta"
                >
                  Get Project Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <a
                  href="tel:+919355744699"
                  className="bg-transparent border-2 border-white/30 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:bg-white/10 inline-flex items-center justify-center"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Talk to Expert
                </a>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <img 
                src="/images/assets/scaffolding-rental-hero.PNG"
                alt="Scaffolding rental services"
                className="rounded-2xl shadow-2xl w-full max-w-xs sm:max-w-sm lg:max-w-md object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Rent */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Why Rent <span className="text-yellow-500">Scaffolding?</span>
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                For most construction projects, renting scaffolding is more cost-effective and practical than buying.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {features.map((feature) => (
                  <div key={feature} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-6">Rental vs Buying</h3>
              <div className="space-y-4">
                {[
                  { label: "Capital Lock-in", value: "None with Rental" },
                  { label: "Maintenance", value: "Included" },
                  { label: "Storage", value: "Not Required" },
                  { label: "Flexibility", value: "Scale Up/Down" },
                  { label: "Quality Assurance", value: "ISO Certified" }
                ].map((item) => (
                  <div key={item.label} className="flex justify-between border-b border-slate-200 pb-3">
                    <span className="text-slate-600">{item.label}</span>
                    <span className="font-semibold text-yellow-600">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scaffolding Types */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Types of Scaffolding Available</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {scaffoldingTypes.map((type) => (
              <div key={type.name} className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-slate-900 mb-4">{type.name}</h3>
                <p className="text-slate-600 mb-6">{type.description}</p>
                <ul className="space-y-2 mb-6">
                  {type.features.map((feature) => (
                    <li key={feature} className="text-sm text-slate-600 flex items-center">
                      <CheckCircle className="w-4 h-4 text-yellow-500 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={type.link}
                  className="inline-flex items-center text-yellow-600 font-semibold hover:text-yellow-700"
                >
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Industries We Serve</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {industries.map((industry) => (
              <div key={industry.name} className="bg-slate-800/50 rounded-xl p-6 text-center border border-slate-700">
                <industry.icon className="w-10 h-10 text-yellow-500 mx-auto mb-4" />
                <h3 className="font-semibold text-white text-sm">{industry.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* City Links */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Scaffolding Rental by City</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: "Noida", slug: "noida" },
              { name: "Delhi NCR", slug: "delhi-ncr" },
              { name: "Hyderabad", slug: "hyderabad" },
              { name: "Vizag", slug: "vizag" }
            ].map((city) => (
              <Link
                key={city.slug}
                to={`/scaffolding-rental-${city.slug}`}
                className="p-6 bg-slate-50 rounded-xl text-center hover:bg-yellow-50 hover:border-yellow-500 border border-transparent transition-all"
              >
                <span className="font-bold text-slate-900">{city.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm" data-testid={`faq-${index}`}>
                <button
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                  onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                >
                  <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">Ready to Get Started?</h2>
          <p className="text-slate-700 mb-8 max-w-xl mx-auto">
            Get a customized quote for your scaffolding requirements
          </p>
          <Link
            to="/contact"
            className="inline-block bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-slate-800 transition-all duration-300 hover:scale-105"
            data-testid="bottom-cta"
          >
            Get Free Quote
          </Link>
        </div>
      </section>
    </div>
  );
}
